import { useCallback, useEffect, useRef, useState } from "react";
import { REFRESH_INTERVAL_MS } from "./usePlacingBook";
import { useAuth } from "../AuthContext";
import { getTokenOrganisationIds } from "../utils/ownership";
import {
  compareQuotes,
  getPlacement,
  getPlacementForDocument,
  getSubmission,
  getSubmissionForDocument,
  getSubmissionsForPlacement,
  getPlacementLines,
  listAll,
  listQuoteRequests,
  pageItems,
} from "../services/PlacingAPI";

// "Not there / not yours" answers while looking a placement up
const isMissing = (err) => [403, 404].includes(err?.response?.status);

/**
 * Loads everything the placing screens need for one placement, found by
 * placement ID or by document ID, and works out whether the user is the
 * broker (an organisation of theirs owns it) or a carrier it was sent to.
 *
 * Returns { loading, error, placement, submission, role, comparison, lines,
 * myQuoteRequests, myLineRequests, reload }. role is "broker", "carrier" or null (nothing yet).
 * comparison is loaded for a broker once the submission is shared;
 * myQuoteRequests are a carrier's quote requests for this submission.
 *
 * With autoRefresh it reloads quietly every minute while the page is
 * visible, and when the user comes back to the tab (as the placing portal
 * does), except while paused (e.g. a form is open). Also returns
 * refreshing and lastUpdated.
 */
export const usePlacement = ({ placementId, documentId, refreshKey, autoRefresh = false, paused = false } = {}) => {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, refreshing: false, lastUpdated: null });
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return; // don't stack refreshes on a slow API
    inFlight.current = true;
    setState((s) => (s.loading ? s : { ...s, refreshing: true }));
    const myOrgIds = getTokenOrganisationIds(user);
    try {
      let placement = null;
      let submission = null;

      try {
        if (placementId) placement = await getPlacement(placementId);
        else if (documentId) placement = await getPlacementForDocument(documentId);
      } catch (err) {
        if (!isMissing(err)) throw err; // a carrier can't see the broker's placement
      }

      if (placement?._id) {
        const subs = pageItems(await getSubmissionsForPlacement(placement._id));
        if (subs[0]?._id) submission = await getSubmission(subs[0]._id);
      } else if (documentId) {
        try {
          submission = await getSubmissionForDocument(documentId);
        } catch (err) {
          if (!isMissing(err)) throw err;
        }
      }
      if (!submission?._id) submission = null;

      const owner = submission?.organisationOwnerId || placement?.organisationOwnerId;
      const role = !placement && !submission ? null : myOrgIds.includes(owner) ? "broker" : "carrier";

      // A carrier opens the submission by id: that records it as viewed and,
      // the first time, gives the carrier its own Codify setting and run
      if (role === "carrier" && submission?._id && !placement) {
        submission = (await getSubmission(submission._id)) || submission;
      }

      let comparison = null;
      let lines = null;
      let myQuoteRequests = [];
      let myLineRequests = [];
      if (role === "broker" && submission?.status === "shared") {
        comparison = await compareQuotes(submission._id);
        // Order: the lines requested so far (none until a lead is nominated)
        lines = placement?._id ? await getPlacementLines(placement._id).catch(() => null) : null;
      }
      if (role === "carrier" && submission) {
        myQuoteRequests = pageItems(await listQuoteRequests({ pageSize: 100 })).filter(
          (q) => q.submissionId === submission._id && !myOrgIds.includes(q.organisationOwnerId)
        );
        myLineRequests = (await listAll("linerequests").catch(() => [])).filter(
          (r) => r.submissionId === submission._id && !myOrgIds.includes(r.organisationOwnerId)
        );
      }

      setState({ loading: false, refreshing: false, lastUpdated: new Date(), error: null, placement, submission, role, comparison, lines, myQuoteRequests, myLineRequests });
    } catch (err) {
      // A failed background refresh keeps the last good data
      setState((prev) => ({ ...prev, loading: false, refreshing: false, error: prev.placement || prev.submission ? null : err, refreshError: err }));
    } finally {
      inFlight.current = false;
    }
    // user changes only on login/logout
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementId, documentId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!autoRefresh || paused) return undefined;
    const isVisible = () => document.visibilityState === "visible";
    const timer = setInterval(() => isVisible() && load(), REFRESH_INTERVAL_MS);
    const onVisibilityChange = () => isVisible() && load();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [autoRefresh, paused, load]);

  return { ...state, reload: load };
};
